/**
 * [C22, fork B] Customer-mode helpers shared by Dealing in Dollar, the Currency Vault and the quick operation:
 *
 *  - `useNewCustomerName()` — D-017 / D-018: a name typed for "+ إضافة حساب جديد" is checked against the office's
 *    accounts (`GET /customers/check-name`, archived included, D-072). No match → a new person; matches → the
 *    same-name prompt (`DuplicateNameModal`, D03 / fork A): "this person" uses that person's existing account of the
 *    wanted kind, or opens it for them (`personId`), "change name" / cancel abandon. The account is then opened
 *    **inside** the operation (`newAccounts`, `"new:<ref>"`) and the commit needs the PIN of a `create_customers`
 *    holder (own or supervisor) — the server checks it.
 *  - `useExcessFlow()` — D-218: the old excess dialogs (`ExcessTrustModal`, `ExcessDebtModal`) opened when the engine
 *    answers `SURPLUS_ACCOUNT_REQUIRED`; each option becomes the engine's `surplus` option of the **same** operation
 *    (`{kind:"cash"}` or `{kind:"account", accountId}`), with `newAccounts` when the target is opened by it.
 *
 * Every account is addressed by id (D-017); balances are the ledger's strings. The only arithmetic is the display sum
 * of the dialog's "required amount" (balance + surplus, BigInt over decimal strings).
 */
import { useCallback, useRef, useState, type ReactNode } from "react";
import { DuplicateNameModal, ExcessDebtModal, ExcessTrustModal, formatWithCommas, type ExcessAccountOption, type ExcessChoice } from "@faylasuf/ui";
import type { AccountRow, CustomerKind, CustomerNameCheck, NewCustomerAccountSpec } from "../api/types.js";
import { useApp } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";
import { useSession } from "./context.js";
import { useToast } from "./toast.js";

const SCALE = 6;
function toScaled(s: string): bigint {
  const neg = s.startsWith("-");
  const [i = "0", f = ""] = s.replace(/^-/, "").split(".");
  const v = BigInt(i || "0") * 10n ** BigInt(SCALE) + BigInt((f + "000000").slice(0, SCALE) || "0");
  return neg ? -v : v;
}
function fromScaled(v: bigint): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const base = 10n ** BigInt(SCALE);
  const frac = (a % base).toString().padStart(SCALE, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${(a / base).toString()}${frac ? `.${frac}` : ""}`;
}
/** Display-only difference a − b, floored at zero (partial-payment redistribution). */
export const decClampSub = (a: string, b: string): string => {
  const r = toScaled(a) - toScaled(b);
  return fromScaled(r < 0n ? 0n : r);
};
/** Display-only sum of two decimal strings. */
export const decAdd = (a: string, b: string): string => fromScaled(toScaled(a) + toScaled(b));

const kindOfAccount = (a: AccountRow): CustomerKind | null => (a.kind === "customer_debt" ? "debt" : a.kind === "customer_trust" ? "trust" : null);
const ledgerKind = (k: CustomerKind) => (k === "debt" ? "customer_debt" : "customer_trust");

/** Where a customer amount goes: an existing ledger account, or one opened inside the operation. */
export interface CustomerTarget {
  /** The value to put in the engine input (`accountId` or `"new:<ref>"`). */
  readonly accountId: string;
  readonly newAccounts: readonly NewCustomerAccountSpec[];
  /** Display name, for the PIN message. */
  readonly label: string;
  /** Present when an account is opened: what the PIN confirms (D-018). */
  readonly newAccount?: { readonly name: string; readonly kind: CustomerKind };
}

/** The person's own account of `kind` in `currency`, if any (active). */
export function personAccount(accounts: readonly AccountRow[], personId: string | null | undefined, kind: CustomerKind, currency: string): AccountRow | undefined {
  if (!personId) return undefined;
  return accounts.find((a) => a.ownerId === personId && a.kind === ledgerKind(kind) && a.currency === currency && !a.archived);
}

// ---------------------------------------------------------------------------
// New account by name (same-name prompt)
// ---------------------------------------------------------------------------

export function useNewCustomerName(accounts: readonly AccountRow[]): {
  readonly resolve: (name: string, kind: CustomerKind, currency: "IQD" | "USD", ref: string) => Promise<CustomerTarget | null>;
  readonly dialog: ReactNode;
} {
  const { source, t } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const [prompt, setPrompt] = useState<{ name: string; check: CustomerNameCheck; kind: CustomerKind; currency: "IQD" | "USD"; ref: string } | null>(null);
  const pending = useRef<((v: CustomerTarget | null) => void) | null>(null);

  const finish = useCallback((v: CustomerTarget | null) => {
    setPrompt(null);
    const r = pending.current;
    pending.current = null;
    r?.(v);
  }, []);

  const resolve = useCallback(
    async (name: string, kind: CustomerKind, currency: "IQD" | "USD", ref: string): Promise<CustomerTarget | null> => {
      const clean = name.trim();
      if (!clean) return null;
      let check: CustomerNameCheck;
      try {
        check = await source.customerNameCheck(clean);
      } catch (e) {
        if (isSessionLost(e)) sessionLost();
        else toast(errorMessage(t, e), "error");
        return null;
      }
      if (check.matches.length === 0) {
        return { accountId: `new:${ref}`, newAccounts: [{ ref, kind, currency, name: clean }], label: clean, newAccount: { name: clean, kind } };
      }
      return new Promise<CustomerTarget | null>((res) => {
        pending.current = res;
        setPrompt({ name: clean, check, kind, currency, ref });
      });
    },
    [source, sessionLost, toast, t],
  );

  const forPerson = (matchId: string) => {
    if (!prompt) return;
    const m = prompt.check.matches.find((x) => x.id === matchId);
    if (!m) return finish(null);
    if (m.archived) {
      toast(t("error.CUSTOMER_ARCHIVED"), "error"); // restore belongs to Customer Accounts (D-072)
      return finish(null);
    }
    const existing = personAccount(accounts, m.personId, prompt.kind, prompt.currency);
    if (existing) return finish({ accountId: existing.id, newAccounts: [], label: existing.name ?? m.name });
    finish({ accountId: `new:${prompt.ref}`, newAccounts: [{ ref: prompt.ref, kind: prompt.kind, currency: prompt.currency, personId: m.personId }], label: m.name, newAccount: { name: m.name, kind: prompt.kind } });
  };

  const dialog = prompt ? (
    <DuplicateNameModal
      name={prompt.name}
      matches={prompt.check.matches.map((m) => ({ id: m.id, name: m.name, kindLabel: t(m.kind === "debt" ? "customer.kind_debt" : "customer.kind_trust"), accountNumber: m.accountNumber, archived: m.archived }))}
      onOpenForSamePerson={forPerson}
      onChangeName={() => finish(null)}
      onCancel={() => finish(null)}
    />
  ) : null;

  return { resolve, dialog };
}

// ---------------------------------------------------------------------------
// Excess dialogs (D-218)
// ---------------------------------------------------------------------------

export interface ExcessRequest {
  /** The primary customer ledger account the operation decreases beyond its balance. */
  readonly account: AccountRow;
  /** `SURPLUS_ACCOUNT_REQUIRED.params.surplus` (decimal string). */
  readonly surplus: string;
  /** Labels of the old dialogs per screen (D02 overrides the trust title, `CurrencyVaultView.tsx:3675-3677`). */
  readonly title?: string;
  readonly description?: string;
  readonly actionTypeLabel?: string;
}

export interface ExcessResolution {
  /** The engine's `surplus` option for the counterparty. */
  readonly surplus: { readonly kind: "cash" } | { readonly kind: "account"; readonly accountId: string };
  readonly newAccounts: readonly NewCustomerAccountSpec[];
  /** For the PIN message: where the excess goes. */
  readonly targetLabel: string;
  readonly newAccount?: { readonly name: string; readonly kind: CustomerKind };
}

export function useExcessFlow(opts: {
  readonly accounts: readonly AccountRow[];
  /** "Reduce" (old option 1): the container refills its form so the account part equals the balance. */
  readonly onReduce: (balance: string) => void;
  /** An option was chosen: the container previews / commits the same operation with this surplus option. */
  readonly onResolved: (res: ExcessResolution) => void;
}): { readonly open: (req: ExcessRequest) => void; readonly dialog: ReactNode } {
  const { t } = useApp();
  const toast = useToast();
  const [req, setReq] = useState<ExcessRequest | null>(null);
  const names = useNewCustomerName(opts.accounts);

  const open = useCallback((r: ExcessRequest) => setReq(r), []);
  const close = () => setReq(null);

  const accountChoice = async (choice: ExcessChoice | null, kind: CustomerKind, currency: "IQD" | "USD", r: ExcessRequest): Promise<void> => {
    if (!choice) return void toast(t("excess.select_account_first"), "error");
    if (choice.kind === "new") {
      const target = await names.resolve(choice.name, kind, currency, "s");
      if (!target) return;
      close();
      return opts.onResolved({ surplus: { kind: "account", accountId: target.accountId }, newAccounts: target.newAccounts, targetLabel: target.label, ...(target.newAccount ? { newAccount: target.newAccount } : {}) });
    }
    if (choice.id.startsWith("person:")) {
      const personId = choice.id.slice("person:".length);
      const existing = personAccount(opts.accounts, personId, kind, currency);
      const name = opts.accounts.find((a) => a.ownerId === personId)?.name ?? r.account.name ?? "";
      close();
      if (existing) return opts.onResolved({ surplus: { kind: "account", accountId: existing.id }, newAccounts: [], targetLabel: existing.name ?? name });
      return opts.onResolved({ surplus: { kind: "account", accountId: "new:s" }, newAccounts: [{ ref: "s", kind, currency, personId }], targetLabel: name, newAccount: { name, kind } });
    }
    const acc = opts.accounts.find((a) => a.id === choice.id);
    close();
    opts.onResolved({ surplus: { kind: "account", accountId: choice.id }, newAccounts: [], targetLabel: acc?.name ?? "" });
  };

  let dialog: ReactNode = null;
  if (req) {
    const currency = (req.account.currency === "USD" ? "USD" : "IQD") as "IQD" | "USD";
    const curr = currency === "USD" ? "$" : "د.ع";
    const list = (kind: CustomerKind, exceptId?: string): ExcessAccountOption[] =>
      opts.accounts
        .filter((a) => a.kind === ledgerKind(kind) && a.currency === currency && !a.archived && a.id !== exceptId)
        .map((a) => ({ id: a.id, name: a.name ?? "", balanceLabel: formatWithCommas(a.balance) }));
    const balance = req.account.balance;
    const kind = kindOfAccount(req.account);
    const common = { open: true, onClose: close, customerName: req.account.name ?? "", currencyLabel: curr, ...(req.title ? { title: req.title } : {}), ...(req.description ? { description: req.description } : {}), ...(req.actionTypeLabel ? { actionTypeLabel: req.actionTypeLabel } : {}) };
    if (kind === "trust") {
      const ownDebt = personAccount(opts.accounts, req.account.ownerId, "debt", currency);
      dialog = (
        <ExcessTrustModal
          {...common}
          trustAmountLabel={formatWithCommas(balance)}
          requiredAmountLabel={formatWithCommas(decAdd(balance, req.surplus))}
          excessAmountLabel={formatWithCommas(req.surplus)}
          trustOptions={list("trust", req.account.id)}
          debtOptions={list("debt")}
          // old pre-selection: the customer's own name (App.tsx:6229) — the same person's debt, opened if missing
          defaultDebt={ownDebt ? { id: ownDebt.id, name: ownDebt.name ?? "" } : req.account.ownerId ? { id: `person:${req.account.ownerId}`, name: req.account.name ?? "" } : null}
          onReduce={() => {
            close();
            opts.onReduce(balance);
          }}
          onPayFromOtherTrust={(c) => (c ? void accountChoice(c, "trust", currency, req) : toast(t("excess.select_other_first"), "error"))}
          onCash={() => {
            close();
            opts.onResolved({ surplus: { kind: "cash" }, newAccounts: [], targetLabel: t("excess.trust.cash_title") });
          }}
          onAsDebt={(c) => void accountChoice(c, "debt", currency, req)}
        />
      );
    } else {
      const persons = new Map<string, string>();
      for (const a of opts.accounts) if ((a.kind === "customer_debt" || a.kind === "customer_trust") && !a.archived && a.ownerId && !persons.has(a.ownerId)) persons.set(a.ownerId, a.name ?? "");
      dialog = (
        <ExcessDebtModal
          {...common}
          debtAmountLabel={formatWithCommas(balance)}
          operationAmountLabel={formatWithCommas(decAdd(balance, req.surplus))}
          excessAmountLabel={formatWithCommas(req.surplus)}
          debtOptions={list("debt", req.account.id)}
          trustTargets={[...persons.entries()].map(([id, name]) => ({ id: `person:${id}`, name }))}
          sameCustomerKey={`person:${req.account.ownerId ?? ""}`}
          onWithdrawCash={() => {
            close();
            opts.onResolved({ surplus: { kind: "cash" }, newAccounts: [], targetLabel: t("excess.debt.cash_title") });
          }}
          onPayOtherCustomer={(c) => (c ? void accountChoice(c, "debt", currency, req) : toast(t("excess.select_other_first"), "error"))}
          onDepositAsTrust={(c) => void accountChoice(c, "trust", currency, req)}
        />
      );
    }
  }
  return {
    open,
    dialog: (
      <>
        {dialog}
        {names.dialog}
      </>
    ),
  };
}

/** The PIN message for an operation that opens an account (D-018) or routes an excess, else `fallback`. */
export function customerPinMessage(t: (k: string, p?: Record<string, string | number>) => string, fallback: string, opts: { newAccounts?: readonly { name: string; kind: CustomerKind }[]; excess?: { amount: string; currency: string; target: string } }): string {
  const parts: string[] = [];
  for (const n of opts.newAccounts ?? []) parts.push(t("pin.message_new_customer", { kind: t(n.kind === "debt" ? "customer.kind_debt" : "customer.kind_trust"), name: n.name }));
  if (opts.excess) parts.push(t("pin.message_surplus", { excess: formatWithCommas(opts.excess.amount), curr: opts.excess.currency, target: opts.excess.target }));
  return parts.length > 0 ? `${fallback}\n${parts.join("\n")}` : fallback;
}

/** A placeholder `AccountRow` for an account the operation would open (`"new:<ref>"`), so the excess dialog can name it. */
export function syntheticAccount(id: string, name: string, kind: CustomerKind, currency: string, personId: string | null): AccountRow {
  return {
    id, branchId: "", kind: ledgerKind(kind), currency, name, category: null, ownerId: personId, parentAccountId: null, archived: false,
    balance: "0", quantity: "0", costIqd: "0", reserved: "0", available: "0", total: "0",
  };
}

/** The primary account of a SURPLUS_ACCOUNT_REQUIRED refusal: the ledger row, or the placeholder of a new account. */
export function surplusPrimary(accounts: readonly AccountRow[], accountId: string, target: CustomerTarget | null, kind: CustomerKind, currency: string): AccountRow | undefined {
  const row = accounts.find((a) => a.id === accountId);
  if (row) return row;
  if (target && target.accountId === accountId) {
    const personId = target.newAccounts.find((n) => `new:${n.ref}` === accountId)?.personId ?? null;
    return syntheticAccount(accountId, target.label, kind, currency, personId);
  }
  return undefined;
}
